/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';

import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getIsBot, getOfferGatewayData, getOfferInboxData } from '../../../state/ducks/App/App-Selectors';
import { getProfileInfo, getUserType } from '../../../state/ducks/App/ducks/Common/Common-Selectors';
import useGetAccessTokenSafely from '../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../gql';
import useBrowserUUID from '../useBrowserUUID';
import { getIsAuthenticatedStatus, getUserEmailId } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportData, getPassportSubscriptionStatus } from '../../../state/ducks/Passport/Passport-Selectors';
import validatePassport from '../validatePassport';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import findOfferGatewaySF from '../../gql/queries/findOfferGatewaySF';

/**
 * @description - used to communicate with customer-personalize API for personalized content.
 * @param { string } interactionName - salesforce interaction name
 * @param {object} targeting -
 * @param { object } apolloOptions - overrides | additional options to pass to useQuery API
 * @param {boolean} optInHoldoutParticipation - overrides holdout participation for specific UI/Components
 * @returns { object } - { loading, error, data, variables } -> add variables.skip to loading handling and variables.eidError to error handling
 */

const useSalesforceOfferGatewayQuery = ({
    interactionName,
    targeting,
    apolloOptions = {},
    queryName = 'OfferGateway',
    featureFlag = true, // default but negated to skip if statement
    isSessionHasAccount = false, // needed for checking if guest user has account
    optInHoldoutParticipation = false,
    resolveIdentity = false,
}) => {
    const isBot = useSelector(getIsBot);
    const isSalesForceRecsEnabled = useSelector(getFeatureFlag('is-salesforce-recs-enabled'));
    const offerGateway = useSelector(getOfferGatewayData);
    const offerInbox = useSelector(getOfferInboxData);

    // if ff true negate make false and skip statement and make api call / if false negate and make true to return from call and continue
    if (isBot || !featureFlag || !isSalesForceRecsEnabled) {
        return {
            loading: false, error: false, data: null, variables: { skip: false },
        };
    }

    const query = findOfferGatewaySF(queryName);
    const brand = useSelector(getBrandName) || '1800flowers';
    const emailId = useSelector(getUserEmailId);
    const isPersonalizationHoldoutEnabled = optInHoldoutParticipation ? useSelector(getFeatureFlag('is-personalization-holdout-enabled')) : optInHoldoutParticipation;
    const browserUUID = useBrowserUUID();

    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    let sessionCustomerType;
    if ((isPassportMember && isAuthenticated) || isPassportAdded) {
        sessionCustomerType = 'P'; // Passport members > Registered users
    } else if (isAuthenticated) {
        sessionCustomerType = 'R'; // Registered user, but not a Passport member
    } else {
        sessionCustomerType = 'G'; // Guest
    }

    const user = { anonymousId: browserUUID };
    if (offerGateway?.email || emailId) {
        user.emailId = offerGateway?.email || emailId;
    } else if (offerGateway?.phone) {
        user.phone = offerGateway?.phone;
    }

    // set sessionLoginState according to user login state
    const userType = useSelector(getUserType);
    const userAttributes = [];
    let sessionOGOptin = `${offerGateway?.offerGatewayOptin || offerInbox?.offerGatewayOptin}`;
    let sessionOGLoggedIn = 'false';
    let sessionOGAccountCreation = 'false';
    const userInfo = useSelector(getProfileInfo);
    userAttributes.push({ name: 'sessionLoginState', value: userType === 'R' || userType === 'P' ? 'true' : 'false' });
    userAttributes.push({ name: 'sessionCustomerType', value: sessionCustomerType });
    if (userInfo?.offerGatewayOptin) {
        sessionOGOptin = userInfo.offerGatewayOptin === 'true';
        sessionOGLoggedIn = `${userInfo?.loginsCount > 1}`;
        sessionOGAccountCreation = `${userInfo?.loginsCount === 1}`;
    }

    userAttributes.push({ name: 'sessionOGOptIn', value: `${sessionOGOptin}` });
    userAttributes.push({ name: 'sessionOGLoggedIn', value: `${sessionOGLoggedIn}` });
    userAttributes.push({ name: 'sessionOGAccountCreation', value: `${sessionOGAccountCreation}` });
    userAttributes.push({ name: 'sessionOGOfferUnlock', value: `${offerGateway?.offerUnlocked}` });

    userAttributes.push({ name: 'sessionOGOptInSMS', value: `${offerGateway?.offerGatewayOptinSMS}` });
    userAttributes.push({ name: 'sessionOGOfferUnlockSMS', value: `${offerGateway?.offerUnlockedSMS}` });

    // code block for sending hasAccountCookie key under userAttributes
    if (isSessionHasAccount) userAttributes.push({ name: 'hasAccountCookie', value: 'true' });

    const jwt = useGetAccessTokenSafely();
    // skip until jwt is loading
    const skip = Boolean(!jwt || !browserUUID);

    return useQuery(query, {
        variables: (skip) ? { skip } : {
            // use default variables directly
            brand,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            resolveIdentity,
            user,
            userAttributes,
            targeting: {
                interactionName,
                ...targeting,
            },
        },
        context: { api: 'personalizedContent', jwt },
        fetchPolicy: 'no-cache',
        skip,
        ...apolloOptions,
    });
};

export default useSalesforceOfferGatewayQuery;
